import type { Locale } from '@kovix/shell-kit/i18n';
import type { NavGroup } from '@/shared/config/navigation';

/**
 * Chuỗi riêng của khung Web Admin theo mockup v152 (01/10/2026) — bổ sung cho từ điển `shell.*` của shell-kit
 * (khóa thiếu ở `t()` trả về chính khóa, nên chuỗi mới của khung để ở đây, vi + en, không đụng shell-kit).
 */
const TEXT = {
  opsCenter: { vi: 'Trung tâm vận hành', en: 'Operations center' },
  opsCenterHint: { vi: 'Về Trung tâm vận hành', en: 'Back to the operations center' },
  homeHint: { vi: 'Về trang mặc định', en: 'Back to the default page' },
  crumbTenant: { vi: 'Vận hành', en: 'Operations' },
  crumbPlatform: { vi: 'Nền tảng', en: 'Platform' },
  searchPlaceholder: {
    vi: 'Tìm đơn hàng, khách hàng, sản phẩm, SKU…',
    en: 'Search orders, customers, products, SKUs…',
  },
  portal: { vi: 'Customer & CTV Portal', en: 'Customer & CTV Portal' },
  groupToggle: { vi: 'Thu gọn / mở nhóm {group}', en: 'Collapse / expand group {group}' },
  itemsUnit: { vi: 'mục', en: 'items' },
  accountMenu: { vi: 'Mở menu tài khoản', en: 'Open account menu' },
  profile: { vi: 'Hồ sơ cá nhân', en: 'My profile' },
  profileHint: {
    vi: 'Thông tin tài khoản, phân quyền, đổi mật khẩu/MFA',
    en: 'Account details, permissions, password/MFA',
  },
  security: { vi: 'Bảo mật tài khoản', en: 'Account security' },
  loginHistory: { vi: 'Nhật ký đăng nhập', en: 'Sign-in history' },
  devices: { vi: 'Thiết bị đăng nhập', en: 'Signed-in devices' },
  notifyCenter: { vi: 'Trung tâm thông báo', en: 'Notification center' },
  unread: { vi: '{n} thông báo chưa đọc', en: '{n} unread notifications' },
  tenantWorkspace: { vi: 'Tenant workspace', en: 'Tenant workspace' },
  platformWorkspace: { vi: 'Nền tảng SaaS', en: 'SaaS platform' },
} as const;

export type ShellTextKey = keyof typeof TEXT;

export function shellText(key: ShellTextKey, locale: Locale, params?: Record<string, string | number>): string {
  const s: string = TEXT[key][locale] ?? TEXT[key].vi;
  return params ? s.replace(/\{(\w+)\}/g, (_, k: string) => String(params[k] ?? `{${k}}`)) : s;
}

/** Chữ viết tắt avatar như mockup (`kxInitials`): chữ đầu của HAI từ cuối — "Phạm Gia Hưng" → "GH". */
export function initials(name: string | null | undefined): string {
  const parts = (name ?? '').split(/\s+/).filter(Boolean).slice(-2);
  return parts.length
    ? parts
        .map((p) => p.charAt(0))
        .join('')
        .toUpperCase()
    : '?';
}

/** Mục menu đang chọn: khớp đường dẫn dài nhất (giữ nguyên quy tắc cũ của Shell). */
export function isActivePath(pathname: string, path: string): boolean {
  return pathname === path || (path !== '/' && pathname.startsWith(`${path}/`));
}

/** Khóa nhóm ổn định (nhóm gốc `group = null` luôn hiện, không thu/mở). */
export const groupKey = (g: NavGroup): string => g.group ?? '_root';

/** Trang "Trung tâm vận hành" (fbOps, v145/v152) chỉ khi mục `/ops` còn lại sau filterNav — không thì về trang mặc định `/`. */
export function homePathOf(nav: NavGroup[]): string {
  return nav.some((g) => g.items.some((i) => i.path === '/ops')) ? '/ops' : '/';
}

/**
 * R5.9 — tenantAdmin/executive vào app ở `/` thì chuyển sang Trung tâm vận hành F&B MỘT lần (trang khởi đầu). Bấm menu "Tổng quan" sau đó
 * phải mở đúng trang Tổng quan, không bị đẩy lại `/ops`. Rời `/` (Shell gọi khi đổi trang) hoặc đã chuyển một lần → hết lượt.
 */
let opsLandingPending = true;
export function endOpsLanding(): void {
  opsLandingPending = false;
}
export function takeOpsLanding(): boolean {
  const pending = opsLandingPending;
  opsLandingPending = false;
  return pending;
}
